import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva } from "class-variance-authority";
import { X } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { isNativeApp } from "@/lib/nativeApp"
import { useSettings } from "@/context/SettingsContext"

const CLOSE = { en: "Close", fr: "Fermer", es: "Cerrar", pt: "Fechar", ar: "إغلاق", zh: "关闭" };

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-overlay bg-touki-black/60 duration-slow data-[state=closed]:duration-exit-slow data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref} />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "tk-motion-slide fixed z-dialog gap-4 overscroll-contain bg-background p-6 shadow-tk-dialog data-[state=open]:duration-slow data-[state=closed]:duration-exit-slow data-[state=open]:ease-tk-out data-[state=closed]:ease-tk-in data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

const SheetContent = React.forwardRef(({ side = "right", className, children, onOpenAutoFocus, onCloseAutoFocus, hideClose = false, ...props }, ref) => {
  const settings = useSettings();
  // A sheet opened by a plain button (not a SheetTrigger) has no trigger for
  // Radix to return focus to: remember what had focus and give it back.
  const opener = React.useRef(null);
  // In the apps a bottom sheet has rounded top corners, a grab handle and the
  // sheet shadow (index.css tk-sheet-bottom); the website's stays as it is.
  const appSheet = side === "bottom" && isNativeApp();
  return (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), appSheet && "tk-sheet-bottom", className)}
      onOpenAutoFocus={(event) => {
        opener.current = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
        onOpenAutoFocus?.(event);
      }}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        if (event.defaultPrevented) return;
        const back = opener.current;
        if (back && back.isConnected) {
          event.preventDefault();
          back.focus();
        }
      }}
      {...props}>
      {/* A sheet that draws its own close in its title row (the apps' AppSheet) hides this one. */}
      {hideClose ? null : <SheetPrimitive.Close
        className="absolute end-3 top-3 inline-flex size-11 items-center justify-center rounded-full text-touki-black transition-colors duration-fast hover:bg-touki-light disabled:pointer-events-none">
        <X weight="bold" aria-hidden="true" className="size-5" />
        <span className="sr-only">{CLOSE[settings?.lang] || CLOSE.en}</span>
      </SheetPrimitive.Close>}
      {appSheet ? <div className="tk-sheet-handle" aria-hidden="true" data-testid="sheet-handle" /> : null}
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
  );
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-start", className)}
    {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-touki-black", className)}
    {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-body-sm text-touki-muted", className)}
    {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
